import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DataProvider } from './context/DataContext';
import Layout from './components/layout/Layout';
import Login from './pages/public/Login';
import Pay from './pages/public/Pay';
import LmsShortcut from './pages/public/LmsShortcut';
import GuestDashboard from './pages/public/GuestDashboard';
import StudentSignup from './pages/public/StudentSignup';
import StudentHome from './pages/student/StudentHome';
import MyCourses from './pages/student/MyCourses';
import CourseOverview from './pages/student/CourseOverview';
import StudentClassroom from './pages/student/Classroom';
import StudentResources from './pages/student/Resources';
import StudentAssignments from './pages/student/Assignments';
import StudentGrades from './pages/student/Grades';
import StudentCalendar from './pages/student/Calendar';
import StudentMessaging from './pages/student/Messaging';
import StudentAnnouncements from './pages/student/Announcements';
import StudentStudyLog from './pages/student/StudyLog';
import StudentEvents from './pages/student/Events';
import StudentAdministration from './pages/student/Administration';
import StudentProfile from './pages/student/Profile';
import StudentHelp from './pages/student/Help';
import StudentGames from './pages/student/Games';
import StudentRegistration from './pages/student/Registration';
import StudentFacultyDetail from './pages/student/FacultyDetail';
import StudentSearch from './pages/student/Search';
import Community from './pages/shared/Community';
import LessonSummaries from './pages/shared/LessonSummaries';
import StudentGoalsTracker from './pages/student/GoalsTracker';
import TeacherDashboard from './pages/teacher/Dashboard';
import TeacherGrading from './pages/teacher/Grading';
import TeacherContent from './pages/teacher/Content';
import TeacherAssignments from './pages/teacher/Assignments';
import TeacherAttendance from './pages/teacher/Attendance';
import TeacherMessaging from './pages/teacher/Messaging';
import AdminDashboard from './pages/admin/Dashboard';
import AdminPipeline from './pages/admin/Pipeline';
import AdminAssets from './pages/admin/Assets';
import Systems from './pages/admin/Systems';
import AdminUsers from './pages/admin/Users';
import AdminFinance from './pages/admin/Finance';
import AdminMessaging from './pages/admin/Messaging';
import AdminHomepage from './pages/admin/Homepage';
import AdminResources from './pages/admin/Resources';
import AdminFaculties from './pages/admin/Faculties';
import AdminSignup from './pages/admin/AdminSignup';
import AdminAcademics from './pages/admin/Academics';
import AdminWebsite from './pages/admin/Website';
import AdminAdvanced from './pages/admin/Advanced';
import Home from './pages/public/Home';
import About from './pages/public/About';
import Faculties from './pages/public/Faculties';
import FacultyDetail from './pages/public/FacultyDetail';
import Admissions from './pages/public/Admissions';
import AdmissionsContact from './pages/public/AdmissionsContact';
import AdmissionsEnquire from './pages/public/AdmissionsEnquire';
import AdmissionsFaqs from './pages/public/AdmissionsFaqs';
import Contact from './pages/public/Contact';
import ContactUpdates from './pages/public/ContactUpdates';
import Events from './pages/public/Events';
import EventSignup from './pages/public/EventSignup';
import PartnershipsSponsorsPage from './pages/public/PartnershipsSponsorsPage';
import Faq from './pages/public/Faq';
import Cart from './pages/public/Cart';
import Register from './pages/public/Register';

const CrownIcon = ({ size = 48 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 7l3 6 4-8 4 8 3-6 2 10H1L3 7z" />
    <path d="M5 19h14" />
  </svg>
);

const roleLandingPath = (role) => ({
  student: '/student/dashboard',
  teacher: '/teacher/dashboard',
  admin: '/admin/dashboard'
}[role] || '/');

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'var(--primary)'
      }}>
        <div style={{ textAlign: 'center', color: 'white' }}>
          <div style={{ marginBottom: '16px' }}>
            <CrownIcon size={48} />
          </div>
          <div style={{ fontSize: '1.25rem', fontFamily: 'var(--font-serif)' }}>School of SHEM</div>
          <div style={{ fontSize: '0.875rem', opacity: 0.8, marginTop: '8px' }}>Loading...</div>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  const userRoles = user.roles || [user.role];
  const activeRole = user.activeRole || user.role;

  if (allowedRoles && !allowedRoles.some(role => userRoles.includes(role))) {
    return <Navigate to={roleLandingPath(activeRole)} replace />;
  }

  const isStudentOnboardingPath =
    location.pathname.startsWith('/student/registration') ||
    location.pathname.startsWith('/student/faculties');
  if (userRoles.includes('student') && allowedRoles?.includes('student') && !user?.lmsActive && !isStudentOnboardingPath) {
    return <Navigate to="/lms" replace />;
  }
  
  return children;
}

function PublicRoute({ children }) {
  const { user } = useAuth();
  
  if (user) {
    return <Navigate to={roleLandingPath(user.activeRole || user.role)} replace />;
  }
  
  return children;
}

function AppRoutes() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [location.pathname]);

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/" element={<Home />} />
      <Route path="/home" element={<Navigate to="/" replace />} />
      <Route path="/landing" element={<Navigate to="/" replace />} />
      <Route path="/about" element={<About />} />
      <Route path="/academics" element={<Navigate to="/academics/faculties" replace />} />
      <Route path="/academics/programs" element={<Navigate to="/academics/faculties" replace />} />
      <Route path="/academics/courses" element={<Navigate to="/academics/faculties" replace />} />
      <Route path="/academics/faculties" element={<Faculties />} />
      <Route path="/academics/faculties/:slug" element={<FacultyDetail />} />
      <Route path="/admissions" element={<Admissions />} />
      <Route path="/admissions/contact" element={<AdmissionsContact />} />
      <Route path="/admissions/enquire" element={<AdmissionsEnquire />} />
      <Route path="/admissions/faqs" element={<AdmissionsFaqs />} />
      <Route path="/events" element={<Events />} />
      <Route path="/events/signup" element={<EventSignup />} />
      <Route path="/apply" element={<Register />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/contact/updates" element={<ContactUpdates />} />
      <Route path="/partnerships" element={<PartnershipsSponsorsPage />} />
      <Route path="/give" element={<Navigate to="/partnerships" replace />} />
      <Route path="/faq" element={<Faq />} />
      <Route path="/cart" element={<Cart />} />
      <Route path="/register" element={<Register />} />
      <Route path="/courses" element={<Navigate to="/academics/courses" replace />} />
      <Route path="/student-signup" element={<Register />} />
      <Route path="/passover-signup" element={<StudentSignup />} />
      <Route path="/pay" element={<Pay />} />
      <Route path="/lms" element={<LmsShortcut />} />
      <Route path="/guest" element={<GuestDashboard />} />

      <Route path="/systems" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <Systems />
          </Layout>
        </ProtectedRoute>
      } />
      
      {/* Student Routes */}
      <Route path="/student/*" element={
        <ProtectedRoute allowedRoles={['student']}>
          <Layout>
            <Routes>
              <Route path="dashboard" element={<StudentHome />} />
              <Route path="courses" element={<MyCourses />} />
              <Route path="courses/:courseId" element={<CourseOverview />} />
              <Route path="classroom" element={<StudentClassroom />} />
              <Route path="resources" element={<StudentResources />} />
              <Route path="assignments" element={<StudentAssignments />} />
              <Route path="grades" element={<StudentGrades />} />
              <Route path="calendar" element={<StudentCalendar />} />
              <Route path="messaging" element={<StudentMessaging />} />
              <Route path="announcements" element={<StudentAnnouncements />} />
              <Route path="lesson-summaries" element={<LessonSummaries />} />
              <Route path="study-log" element={<StudentStudyLog />} />
              <Route path="games" element={<StudentGames />} />
              <Route path="goals" element={<StudentGoalsTracker />} />
              <Route path="registration" element={<StudentRegistration />} />
              <Route path="registration/checkout" element={<StudentRegistration />} />
              <Route path="faculties/:slug" element={<StudentFacultyDetail />} />
              <Route path="search" element={<StudentSearch />} />
              <Route path="community" element={<Community />} />
              <Route path="events" element={<StudentEvents />} />
              <Route path="administration" element={<StudentAdministration />} />
              <Route path="profile" element={<StudentProfile />} />
              <Route path="help" element={<StudentHelp />} />
              <Route path="*" element={<Navigate to="/student/registration" replace />} />
            </Routes>
          </Layout>
        </ProtectedRoute>
      } />
      
      {/* Teacher Routes */}
      <Route path="/teacher/*" element={
        <ProtectedRoute allowedRoles={['teacher']}>
          <Layout>
            <Routes>
              <Route path="dashboard" element={<TeacherDashboard />} />
              <Route path="grading" element={<TeacherGrading />} />
              <Route path="content" element={<TeacherContent />} />
              <Route path="assignments" element={<TeacherAssignments />} />
              <Route path="attendance" element={<TeacherAttendance />} />
              <Route path="messaging" element={<TeacherMessaging />} />
              <Route path="lesson-summaries" element={<LessonSummaries />} />
              <Route path="community" element={<Community />} />
              <Route path="*" element={<Navigate to="/teacher/dashboard" replace />} />
            </Routes>
          </Layout>
        </ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin/*" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <Routes>
              <Route index element={<AdminDashboard />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="students" element={<AdminUsers />} />
              <Route path="academics" element={<AdminAcademics />} />
              <Route path="applications" element={<AdminPipeline />} />
              <Route path="finance" element={<AdminFinance />} />
              <Route path="website" element={<AdminWebsite />} />
              <Route path="advanced" element={<AdminAdvanced />} />
              <Route path="advanced/messages" element={<AdminMessaging />} />
              <Route path="advanced/access" element={<AdminSignup />} />
              <Route path="advanced/systems" element={<Systems />} />
              <Route path="advanced/faculties" element={<AdminFaculties />} />
              <Route path="advanced/homepage-editor" element={<AdminHomepage />} />
              <Route path="advanced/resources" element={<AdminResources />} />
              <Route path="advanced/marketing-assets" element={<AdminAssets />} />
              <Route path="pipeline" element={<Navigate to="/admin/applications" replace />} />
              <Route path="homepage" element={<Navigate to="/admin/website" replace />} />
              <Route path="assets" element={<Navigate to="/admin/website?tab=media" replace />} />
              <Route path="resources" element={<Navigate to="/admin/academics?tab=resources" replace />} />
              <Route path="orders" element={<Navigate to="/admin/finance?tab=orders" replace />} />
              <Route path="payments" element={<Navigate to="/admin/finance" replace />} />
              <Route path="fees" element={<Navigate to="/admin/finance?tab=fees" replace />} />
              <Route path="admin-signup" element={<Navigate to="/admin/advanced/access" replace />} />
              <Route path="faculties" element={<Navigate to="/admin/academics" replace />} />
              <Route path="users" element={<Navigate to="/admin/students" replace />} />
              <Route path="messaging" element={<Navigate to="/admin/advanced/messages" replace />} />
              <Route path="pops" element={<Navigate to="/admin/finance?tab=proofs" replace />} />
              <Route path="*" element={<Navigate to="/admin" replace />} />
            </Routes>
          </Layout>
        </ProtectedRoute>
      } />
      
      {/* 404 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <DataProvider>
          <AppRoutes />
        </DataProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
